import { z } from 'zod';

// Browser-side env is exposed via Vite's `import.meta.env` and frozen at build time.
// Only `VITE_*` vars are visible. Validate eagerly so a misconfigured build crashes
// at boot rather than failing at the first network call.

const envSchema = z.object({
  VITE_API_URL: z.string().url(),
  VITE_SENTRY_DSN: z.string().url().or(z.literal('')).default(''),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production', 'test']).default('development'),

  // Cut-2 stub for tenant identity. The web app operates as a single hardcoded tenant
  // until Phase 5 ships sign-in + sessions; at that point this var is removed and tenant
  // id comes from the authenticated session. See `src/hooks/useTenant.ts` — the hook's
  // body is the only thing that changes when auth lands.
  VITE_TENANT_ID: z.string().uuid(),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
    .join('\n');
  // Throw so the global error boundary surfaces the failure visibly to a developer.
  throw new Error(`Invalid build-time environment:\n${issues}`);
}

export const env = Object.freeze(parsed.data);

export const isProd = env.VITE_APP_ENV === 'production';
export const isDev = env.VITE_APP_ENV === 'development';
